import LogoPrimary from '@tenant/assets/logo-primary.svg';
import type { FormInstance } from 'antd/es/form';
import { default as Form } from 'antd/es/form';
import { default as Input } from 'antd/es/input';
import { QRCodeSVG } from 'qrcode.react';
import {
  type ComponentType,
  type ReactNode,
  type SVGProps,
  useState
} from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { hasValue } from 'utils/hasValue';

import {
  useGetAuthenticationMfaRequestTotpFactor,
  useGetCustomerCurrentUser,
  usePostAuthenticationMfaVerifyTotpFactor
} from 'api';
import GoogleAuthenticatorLogo from 'assets/images/google-authenticator.svg';
import MicrosoftAuthenticatorLogo from 'assets/images/microsoft-authenticator.svg';
import OTPPhone from 'assets/images/otp-phone.svg';

import { logout } from 'reduxLocal/slices/authSlice';

import { useGoogleAnalyticsDispatcher } from 'hooks/useGoogleAnalyticsDispatcher';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { FlipCard } from '@atoms/FlipCard';
import { Spinner } from '@atoms/Spinner/Spinner';

export const OTP_LENGTH = 6;

export const TOTPSetup = ({
  onSuccess
}: {
  onSuccess: ({ access_token }) => void;
}) => {
  const [form] = Form.useForm();
  const [error, setError] = useState<string | null>(null);
  const { data: currentUser, isLoading: userLoading } =
    useGetCustomerCurrentUser();

  const trackEvent = useGoogleAnalyticsDispatcher();

  const { data, isSuccess, isError } = useGetAuthenticationMfaRequestTotpFactor(
    {
      query: {
        refetchOnWindowFocus: false
      }
    }
  );

  const dispatch = useDispatch();

  const { mutate, isPending } = usePostAuthenticationMfaVerifyTotpFactor({
    request: { withCredentials: true }
  });

  const onFinish = (values: { code: string }) => {
    setError(null);
    mutate(
      { data: { auth_payload: values.code } },
      {
        onSuccess: (data) => {
          if (!hasValue(data?.data?.access_token)) {
            return;
          }
          onSuccess({ access_token: data.data.access_token });
          trackEvent('totp_setup', {
            user: currentUser?.user?.email
          });
        },
        onError: (err) => {
          setError('TOTP validation failed. Please try again.');
          console.error(err);
        }
      }
    );
  };

  const setSearchParams = useSearchParams()[1];

  const handleLogout = async () => {
    setSearchParams('user_logout');
    dispatch(logout());
  };

  if (userLoading) {
    return null;
  }

  return (
    <main className="h-screen overflow-y-auto">
      <div className="flex w-full justify-center p-4 md:p-10">
        <LogoPrimary className="hidden h-auto w-24 md:block" />
      </div>
      <div className="m-auto flex w-3/4 flex-col justify-center 2xl:w-1/2">
        <div className="only-shadow flex flex-col justify-between gap-8 self-center bg-white p-8 md:size-fit md:rounded-2xl">
          {isError ? (
            <div>This user already has an MFA registered</div>
          ) : isSuccess ? (
            <div className="flex gap-8">
              <div className="flex w-1/2">
                <section className="flex flex-col gap-8 text-center text-xs md:text-sm">
                  <h1 className="text-2xl font-semibold text-content-primary">
                    Setup authentication app
                  </h1>
                  <StepInstruction
                    stepNumber={1}
                    title="Download an authenticator app"
                  >
                    <AuthenticatorAppGrid />
                    <InstructionText>
                      Install one of these authenticator apps on your phone:
                    </InstructionText>
                  </StepInstruction>
                  <StepInstruction stepNumber={2} title="Scan the QR code">
                    <InstructionText>
                      Use your phone's camera app or open the authenticator app
                      and scan the QR code shown on the right.
                    </InstructionText>
                  </StepInstruction>
                  <StepInstruction
                    stepNumber={3}
                    title="Enter verification code"
                  >
                    <InstructionText>
                      Enter the 6-digit code from your authenticator app
                    </InstructionText>
                    <VerificationForm
                      error={error}
                      form={form}
                      isPending={isPending}
                      onCancel={handleLogout}
                      onFinish={onFinish}
                    />
                  </StepInstruction>
                </section>
              </div>
              <div className="flex w-1/2 flex-col justify-between gap-8">
                <QRCodeSection value={data?.data} />
                <HowItWorksCard />
              </div>
            </div>
          ) : (
            <Spinner />
          )}
        </div>
      </div>
    </main>
  );
};

type StepInstructionProps = {
  stepNumber: number;
  title: string;
  children: ReactNode;
};

const StepInstruction = ({
  children,
  stepNumber,
  title
}: StepInstructionProps) => (
  <section className="flex flex-col gap-4">
    <div className="flex flex-row items-center gap-4">
      <StepBadge stepNumber={stepNumber} />
      <h3 className="text-lg">{title}</h3>
    </div>
    {children}
  </section>
);

type StepBadgeProps = {
  stepNumber: number;
};

const StepBadge = ({ stepNumber }: StepBadgeProps) => (
  <div className="w-16 rounded-xl border border-solid border-feedback-info-muted bg-feedback-info-surface">
    <p className="text-feedback-info">{`Step ${stepNumber}`}</p>
  </div>
);

type InstructionTextProps = {
  children: ReactNode;
};

export const InstructionText = ({ children }: InstructionTextProps) => (
  <div className="flex flex-row text-start">
    <p className="text text-content-strong">{children}</p>
  </div>
);

const authenticatorApps = [
  {
    icon: MicrosoftAuthenticatorLogo,
    label: 'Microsoft Authenticator'
  },
  {
    icon: GoogleAuthenticatorLogo,
    label: 'Google Authenticator'
  }
] as const;

type AuthenticatorAppCardProps = {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
};

const AuthenticatorAppCard = ({
  icon: Icon,
  label
}: AuthenticatorAppCardProps) => (
  <div className="only-shadow flex w-24 flex-col items-center justify-between rounded-xl p-4">
    <Icon className="size-10" />
    <p className="text text-xs text-content-strong">{label}</p>
  </div>
);

export const AuthenticatorAppGrid = () => (
  <div className="flex h-32 flex-row gap-2">
    {authenticatorApps.map(({ icon, label }) => (
      <AuthenticatorAppCard icon={icon} key={label} label={label} />
    ))}
  </div>
);

type VerificationFormProps = {
  error: string | null;
  form: FormInstance;
  isPending: boolean;
  onCancel: () => void;
  onFinish: (values: { code: string }) => void;
};

export const VerificationForm = ({
  error,
  form,
  isPending,
  onCancel,
  onFinish
}: VerificationFormProps) => (
  <Form form={form} layout="vertical" onFinish={onFinish}>
    <Form.Item
      className="flex"
      name="code"
      rules={[{ required: true, message: 'Please input your code' }]}
    >
      <Input.OTP
        onChange={(value) => {
          if (value?.length === OTP_LENGTH) {
            form.submit();
          }
        }}
        formatter={(str) => str.toUpperCase()}
        size="large"
      />
    </Form.Item>
    <Form.Item>
      <div className="flex gap-2">
        <PrimaryButton block onClick={onCancel} type="default">
          Cancel
        </PrimaryButton>
        <PrimaryButton block htmlType="submit" loading={isPending}>
          Verify
        </PrimaryButton>
      </div>
    </Form.Item>
    {error && <div className="mt-2 text-sm text-feedback-error">{error}</div>}
  </Form>
);

type QRCodeSectionProps = {
  value: string | undefined;
};

const QRCodeSection = ({ value }: QRCodeSectionProps) => {
  const [clickCount, setClickCount] = useState(0);
  const [isSecretVisible, setIsSecretVisible] = useState(false);
  const shouldRender = hasValue(value);

  if (!shouldRender) {
    return (
      <section className="flex w-full flex-col gap-8 text-center text-xs md:text-sm">
        <h2 data-testid="scan-qr-code" className="text text-lg">
          Scan QR code
        </h2>
      </section>
    );
  }

  const handleQRCodeClick = () => {
    const nextCount = clickCount + 1;

    if (nextCount >= 4) {
      setIsSecretVisible((prev) => !prev);
      setClickCount(0);
      return;
    }

    setClickCount(nextCount);
  };

  return (
    <section className="flex w-full flex-col gap-8 text-center text-xs md:text-sm">
      <h2 data-testid="scan-qr-code" className="text text-lg">
        Scan QR code
      </h2>
      <div className="only-shadow m-auto p-4">
        <FlipCard
          back={<SecretValueCard value={value} />}
          front={<QRCodeSVG value={value!} />}
          isFlipped={isSecretVisible}
          onClick={handleQRCodeClick}
        />
      </div>
    </section>
  );
};

type SecretValueCardProps = {
  value: string;
};

const SecretValueCard = ({ value }: SecretValueCardProps) => (
  <div className="flex size-full items-center justify-center rounded-2xl p-4">
    <code className="break-all text-xs text-content-strong">{value}</code>
  </div>
);

export const HowItWorksCard = () => (
  <section className="flex flex-col gap-4 rounded-xl bg-surface-muted p-4">
    <div className="flex-row">
      <p className="text-lg">How it works</p>
    </div>
    <div className="flex flex-row gap-4">
      <div className="flex-col content-center">
        <OTPPhone />
      </div>
      <div className="flex-col content-center">
        <p className="text-content-strong">
          The app will generate a unique 6-digit code that refreshes every 30
          seconds.
        </p>
      </div>
    </div>
  </section>
);
